// WHEN EVERY FINDING ON A RESIDENT IS WAITING ON THE SAME MISSING MDS.
//
// A resident with no completed MDS produces a card per supported item, and
// every one of them ends "when the MDS is completed". One real resident
// rendered SIXTY-EIGHT of them. That is not a worklist: it is the chart's
// problem list restated, with the single actionable fact — there is no MDS —
// buried in the middle of it.
//
// This decides when that whole list can be folded behind one line, and what
// that line should say. It is a separate module rather than four lines inside
// App.js for one reason: the item pattern below has to be tested against real
// prose, and a source grep cannot show that it excludes an F-tag.

/**
 * Is this card blocked on there being no completed MDS?
 *
 * The signal lives in the CURRENT value — "Unknown — no MDS completed", "Not
 * assessed — no MDS completed", "No MDS completed to reflect diagnoses" — and
 * not in the subtype, which is prose and varies per card. The older
 * "initial mds not completed" subtype is still honoured, since cards carrying
 * it predate the current wording.
 */
export function isBlockedOnMds(o) {
  if (!o) return false;
  const current = String(o.currentValue ?? o.current_value ?? o.last_mds_value ?? "");
  return /no\s+mds\b/i.test(current)
    || /mds\s+not\s+completed/i.test(current)
    || /initial mds not completed/i.test(String(o.subtype || ""));
}

// MDS item codes are one or two letters followed by FOUR digits, then up to a
// letter, a digit and one more letter: I0100, GG0170B, K0520B3, O0110H3,
// O0400A, O0110H1b.
//
// FOUR IS LOAD-BEARING. The same prose carries F-tags (F604, F812) and ICD-10
// codes (I69.951, G40.89, F25.8), and all of them have three digits or fewer
// before any separator. Accepting three would turn every F-tag on the card
// into an MDS item a coordinator would then go looking for.
//
// ── THE TRAILING LETTER WAS MISSING, AND SECTION O IS WHERE IT COSTS ───────
//
// `[A-Z]?\d?` stops after the digit, so O0110H1b — the grid cell for an IV
// antibiotic given while a resident — matched NOTHING. Not the wrong item:
// none at all, because `\b` cannot fall between `1` and `b`. Nor could the
// uppercase spelling O0110H1B, which this codebase also writes. So a card
// whose whole point is that item advertised no item, and the card count and
// the item count disagreed by one with nothing to explain it.
//
// It surfaced from making the demo roster production-like — the new admission
// carries an IV antibiotic, six cards resolved to five items, and the one that
// vanished was the only priced finding in the set. Which is the argument for
// fixtures that look like production: the defect was shipped and invisible
// against test prose that only ever used O0110H3.
//
// The final letter may be either case because real item sets use both, and
// four digits still does the work of keeping F-tags and ICD-10 out.
const MDS_ITEM = /\b([A-Z]{1,2}\d{4}[A-Z]?\d?[A-Za-z]?)\b/g;

/** Every distinct MDS item named across these cards' proposed values, sorted. */
export function mdsItemsIn(rows) {
  const found = new Set();
  for (const o of rows || []) {
    const text = String(o?.proposedValue ?? o?.proposed_value ?? "");
    for (const m of text.matchAll(MDS_ITEM)) found.add(m[1]);
  }
  return [...found].sort();
}

/**
 * Should the list collapse, and to what?
 *
 * COLLAPSES ONLY WHEN EVERY CARD IS BLOCKED. On a mixed list the actionable
 * cards are the entire point of the screen, and folding them behind a toggle
 * would hide work rather than noise. A single card is left alone too — there
 * is nothing to summarise and the summary would be longer than the card.
 *
 * THE TWO COUNTS ANSWER DIFFERENT QUESTIONS and are both returned. `cards` is
 * what the scan stored; `items` is what somebody actually has to code. On that
 * resident they were 68 and 14, and the gap between them is duplication —
 * surfaced rather than silently collapsed, because this screen is not the
 * place that gets to decide two cards are the same finding.
 */
export function summariseNoMds(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const allBlocked = list.length > 1 && list.every(isBlockedOnMds);
  return {
    allBlocked,
    cards: list.length,
    items: allBlocked ? mdsItemsIn(list) : [],
  };
}
